/**
 * C.3 — Obsidian Import window.
 *
 * Single Win95-themed surface offering all four import paths plus a
 * shared progress + summary screen. Routes through `lib/vault/importers/*`
 * for the actual file walking and parsing. The Convex side that picks
 * up the resulting ImportResult ships in the vault explorer's read path.
 *
 * UX layout: a 2x2 grid of "path cards", one per import path, each
 * with its own primary action. After a path is picked, the grid is
 * replaced by a streaming progress pane. Completion → summary screen
 * with counts and an explicit "Confirm import" button. Clicking
 * Confirm transitions through `committing` → `committed` (or
 * `error`); the `committed` panel shows the per-table insert counts
 * and an "Open vault" button that focuses /obsidian-vault.
 *
 * Font: WindowFrame defaults to monospace (var(--xp-font-mono)) for window
 * content. This surface overrides to var(--xp-font-body) to match Help /
 * Settings / etc. The override is inline rather than a className so it
 * cascades through nested Tailwind-styled children.
 *
 * NIPE prompt is intentionally NOT inlined here. NIPE is the floating
 * overlay shipping in C.10/C.11; its "first-visit" walkthrough is
 * surfaced by the overlay layer, not embedded in this window.
 */
const WIN_FONT =
  "var(--xp-font-body)";

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { testIds } from '../../lib/testIds';
import {
  buildDragDropSource,
  buildZipSource,
  isShowDirectoryPickerAvailable,
  openFsapVaultPicker,
  walkImportSource,
  type ImportResult,
  type ImportSource,
} from '../../lib/vault/importers';
import { useWindowContext } from '../desktop/WindowContext';
import { SharePointFolderPicker } from './SharePointFolderPicker';
import './ObsidianImportWindow.css';

/**
 * Shape returned by `onConfirmImport` after the host has persisted the
 * parsed vault to Convex. Used by the window to render the `committed`
 * phase ("✓ Imported N notes / L links / A assets") and to enable the
 * "Open vault" navigation button.
 */
export interface ImportCommitResult {
  vaultId: string;
  counts: { notes: number; links: number; assets: number };
}

type Phase =
  | 'choose'
  | 'progress'
  | 'summary'
  | 'committing'
  | 'committed'
  | 'error';

interface ObsidianImportWindowProps {
  /**
   * Hook the host fires when the user confirms the import.
   * Returns the persistence result so the window can render success
   * counts + an "Open vault" button. Throws on failure; the window
   * routes the error message into the `phase: 'error'` panel.
   */
  onConfirmImport?: (result: ImportResult) => Promise<ImportCommitResult>;
  /**
   * Optional override for the SharePoint folder picker. When omitted,
   * the window opens its own bundled SharePointFolderPicker modal.
   */
  onPickSharePointFolder?: () => Promise<ImportSource | null>;
}

export function ObsidianImportWindow({
  onConfirmImport,
  onPickSharePointFolder,
}: ObsidianImportWindowProps) {
  const [phase, setPhase] = useState<Phase>('choose');
  const [progressCount, setProgressCount] = useState(0);
  const [progressLast, setProgressLast] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [sharePointPickerOpen, setSharePointPickerOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const zipInputRef = useRef<HTMLInputElement | null>(null);

  // F-NIPE-DESKTOP-AWARENESS Phase 2 — read-only bridge for
  // readWindowContext('/obsidian-import'). Lets NIPE see whether the
  // user is mid-import + the latest progress count so it can give an
  // accurate status answer instead of guessing.
  const importBridgeRef = useRef({
    phase,
    dropActive,
    progressCount,
    progressLast,
    errorMessage,
    helpOpen,
    sharePointPickerOpen,
  });
  importBridgeRef.current = {
    phase,
    dropActive,
    progressCount,
    progressLast,
    errorMessage,
    helpOpen,
    sharePointPickerOpen,
  };
  useEffect(() => {
    window.__repancyObsidianImport = () => {
      const cur = importBridgeRef.current;
      return {
        phase: String(cur.phase),
        dropActive: cur.dropActive,
        progressCount: cur.progressCount,
        progressLastPath: cur.progressLast,
        errorMessage: cur.errorMessage,
        helpOpen: cur.helpOpen,
        sharePointPickerOpen: cur.sharePointPickerOpen,
      };
    };
    return () => {
      delete window.__repancyObsidianImport;
    };
  }, []);

  // `useWindowContext` is provided by WindowProvider mounted inside
  // Win95Desktop; tests render this window outside the provider, in
  // which case the default no-op openWindow/focusWindow are fine.
  const { openWindow, focusWindow, closeWindow } = useWindowContext();
  const navigate = useNavigate();

  const fsapAvailable = useMemo(() => isShowDirectoryPickerAvailable(), []);

  const runImport = useCallback(
    async (source: ImportSource) => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setPhase('progress');
      setProgressCount(0);
      setProgressLast('');
      setResult(null);
      setErrorMessage(null);
      try {
        const r = await walkImportSource(source, {
          signal: ctrl.signal,
          onProgress: (n, p) => {
            setProgressCount(n);
            setProgressLast(p);
          },
        });
        setResult(r);
        setPhase('summary');
      } catch (err) {
        setErrorMessage((err as Error).message ?? String(err));
        setPhase('error');
      }
    },
    [],
  );

  // Cleanup any in-flight import on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleFsap = useCallback(async () => {
    if (!fsapAvailable) return;
    const source = await openFsapVaultPicker();
    if (source) runImport(source);
  }, [fsapAvailable, runImport]);

  const handleZipPick = useCallback(() => {
    zipInputRef.current?.click();
  }, []);

  const handleZipChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const source = await buildZipSource(file);
        await runImport(source);
      } catch (err) {
        setErrorMessage((err as Error).message ?? String(err));
        setPhase('error');
      }
    },
    [runImport],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDropActive(false);
      const source = buildDragDropSource(e.nativeEvent);
      if (!source) {
        setErrorMessage(
          'Browser did not surface a folder from the drop. Try the "Pick a folder" or "Upload a ZIP" path instead.',
        );
        setPhase('error');
        return;
      }
      // 2026-05-27 — drop fires runImport directly. The previous F5
      // routing through `dispatchPortableSiteDrop` was dead-on-arrival
      // because `NipePortableSiteDropDialog` (the receiver) is
      // commented out in Win95Desktop. We're inside the import window
      // already so the projection picker would only be redundant
      // re-confirmation; the import-summary screen already lets the
      // user choose Confirm vs. Start over.
      void runImport(source);
    },
    [runImport],
  );

  const handleSharePoint = useCallback(async () => {
    // If the host supplied a custom picker, use it; otherwise open the
    // bundled SharePointFolderPicker modal (which calls the same Graph
    // helpers as the rest of the window).
    if (onPickSharePointFolder) {
      try {
        const source = await onPickSharePointFolder();
        if (source) runImport(source);
      } catch (err) {
        setErrorMessage((err as Error).message ?? String(err));
        setPhase('error');
      }
      return;
    }
    setSharePointPickerOpen(true);
  }, [onPickSharePointFolder, runImport]);

  const handleSharePointPicked = useCallback(
    (source: ImportSource) => {
      setSharePointPickerOpen(false);
      runImport(source);
    },
    [runImport],
  );

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    setPhase('choose');
  }, []);

  const handleReset = useCallback(() => {
    setResult(null);
    setCommitResult(null);
    setErrorMessage(null);
    setPhase('choose');
  }, []);

  // Awaits the host's persistence work. Drives the new phase machine:
  //   summary → committing → committed   (success)
  //   summary → committing → error       (host throws)
  // We do NOT call handleReset() here — that wipes the success counts.
  // Reset is now an explicit user action (Open vault / Import another).
  const handleConfirm = useCallback(async () => {
    if (!result) return;
    if (!onConfirmImport) {
      // No host wired in (rare — tests, dev surfaces). Treat as success
      // so the button isn't a permanent no-op, but show zero counts so
      // anyone seeing this knows nothing was persisted.
      setCommitResult({
        vaultId: '',
        counts: { notes: 0, links: 0, assets: 0 },
      });
      setPhase('committed');
      return;
    }
    setErrorMessage(null);
    setPhase('committing');
    try {
      const committed = await onConfirmImport(result);
      setCommitResult(committed);
      setPhase('committed');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : String(err ?? 'unknown error');
      setErrorMessage(msg);
      setPhase('error');
    }
  }, [onConfirmImport, result]);

  const handleOpenVault = useCallback(() => {
    // 2026-05-28 — Idiomatic handoff: navigate with
    // `?vault=<id>` so the vault window picks it up via
    // useLocation parsing on its first render. URL is the
    // race-free, refresh-friendly, shareable source of truth —
    // no sessionStorage / CustomEvent gymnastics needed. We go
    // through TanStack's navigate (not raw history.replaceState)
    // so the router re-renders subscribers and the vault window
    // picks up the new search param.
    if (commitResult?.vaultId) {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          vault: commitResult.vaultId,
        }),
        replace: true,
      });
    }
    openWindow('/obsidian-vault');
    focusWindow('/obsidian-vault');
    closeWindow('/obsidian-import');
    handleReset();
  }, [
    openWindow,
    focusWindow,
    closeWindow,
    handleReset,
    commitResult,
    navigate,
  ]);

  return (
    <div
      data-testid={testIds.obsidianImport.root}
      className="p-4 text-sm text-black h-full overflow-y-auto"
      style={{ fontFamily: WIN_FONT, fontSize: 13, lineHeight: 1.45 }}
    >
      <header className="mb-3">
        <h1 className="text-lg font-bold mb-1">Obsidian Import</h1>
        <p className="text-gray-800">
          This is a web app, not a real desktop. Bring an Obsidian-shaped vault
          (markdown notes with wikilinks, tags, frontmatter) into this portal via
          one of the four paths below. SharePoint is the source of truth once the
          vault is imported.
        </p>
      </header>

      <div className="mb-3">
        <button
          type="button"
          data-testid={testIds.obsidianImport.helpToggle}
          onClick={() => setHelpOpen((x) => !x)}
          className="underline text-blue-800 hover:text-blue-900"
        >
          {helpOpen ? 'Hide guide' : 'Show import guide'}
        </button>
      </div>

      {helpOpen && (
        <section
          data-testid={testIds.obsidianImport.helpPanel}
          className="mb-4 p-3 border border-gray-700 bg-gray-50"
        >
          <h2 className="font-semibold mb-2">Which path should I pick?</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>SharePoint folder picker</strong> — choose a folder inside
              this portal's SharePoint site. Notes round-trip with eTag-gated
              save-back. The canonical path for live mode.
            </li>
            <li>
              <strong>Pick a local folder</strong> — uses the browser's File
              System Access API. Chrome and Edge support it; Safari and Firefox
              do not.
            </li>
            <li>
              <strong>Drag-and-drop a folder</strong> — works in every modern
              browser. Drop the vault folder onto the area below.
            </li>
            <li>
              <strong>Upload a ZIP</strong> — universal fallback. Compress the
              vault folder, upload the archive.
            </li>
          </ul>
        </section>
      )}

      {phase === 'choose' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PathCard
            kind="sharepoint"
            title="From SharePoint"
            description="Pick a folder under this portal's Documents library. Recommended for live mode."
            buttonLabel="Pick a SharePoint folder"
            onClick={handleSharePoint}
          />
          <PathCard
            kind="fsap"
            title="From a local folder"
            description={
              fsapAvailable
                ? 'Browser-native folder picker (Chrome / Edge).'
                : 'Your browser does not support the File System Access API. Use drag-and-drop or ZIP instead.'
            }
            buttonLabel="Pick a folder"
            disabled={!fsapAvailable}
            onClick={handleFsap}
            unsupportedTestId={
              !fsapAvailable ? testIds.obsidianImport.fsapUnsupported : undefined
            }
          />
          <PathCard
            kind="dragdrop"
            title="Drag-and-drop"
            description="Drag a folder anywhere onto the area below. Works in every modern browser."
            buttonLabel=""
          >
            <div
              data-testid={
                dropActive
                  ? testIds.obsidianImport.dropZoneActive
                  : testIds.obsidianImport.dropZone
              }
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mt-2 border-2 border-dashed p-4 text-center ${
                dropActive
                  ? 'border-blue-700 bg-blue-50'
                  : 'border-gray-600 bg-gray-50'
              }`}
            >
              {dropActive ? 'Drop to import' : 'Drop folder here'}
            </div>
          </PathCard>
          <PathCard
            kind="zip"
            title="Upload a ZIP"
            description="Compress the vault folder and upload the archive. Universal fallback."
            buttonLabel="Choose .zip"
            onClick={handleZipPick}
          >
            <input
              ref={zipInputRef}
              type="file"
              accept=".zip,application/zip"
              data-testid={testIds.obsidianImport.zipInput}
              onChange={handleZipChange}
              className="hidden"
            />
          </PathCard>
        </div>
      )}

      {phase === 'progress' && (
        <section
          data-testid={testIds.obsidianImport.progress}
          className="p-4 border border-gray-700 bg-gray-50"
        >
          <p>
            Reading <strong>{progressCount}</strong> file(s)…
          </p>
          <p
            data-testid={testIds.obsidianImport.progressFile}
            className="font-mono text-xs text-gray-800 truncate mt-1"
          >
            {progressLast || 'starting…'}
          </p>
          <button
            type="button"
            data-testid={testIds.obsidianImport.cancel}
            onClick={handleCancel}
            className="mt-3 px-3 py-1 border border-black bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>
        </section>
      )}

      {phase === 'summary' && result && (
        <section
          data-testid={testIds.obsidianImport.summary}
          className="p-4 border border-gray-700 bg-gray-50"
        >
          <h2 className="font-semibold mb-2">Import summary</h2>
          <p className="mb-1">
            Source: <span className="font-mono">{result.sourceLabel}</span>
          </p>
          <ul className="list-disc list-inside space-y-1 mb-3">
            <li>
              <span data-testid={testIds.obsidianImport.summaryNotesCount}>
                {result.notes.length}
              </span>{' '}
              note(s)
            </li>
            <li>
              <span data-testid={testIds.obsidianImport.summaryFlowsCount}>
                {result.flows.length}
              </span>{' '}
              whiteboard(s)
            </li>
            <li>
              <span data-testid={testIds.obsidianImport.summaryAssetsCount}>
                {result.assets.length}
              </span>{' '}
              asset(s)
            </li>
            <li>
              <span data-testid={testIds.obsidianImport.summaryErrorsCount}>
                {result.errors.length}
              </span>{' '}
              error(s)
            </li>
          </ul>
          {result.errors.length > 0 && (
            <details className="mb-3">
              <summary className="cursor-pointer">Show error detail</summary>
              <ul className="list-disc list-inside text-xs text-red-800 mt-1">
                {result.errors.map((e) => (
                  <li key={e.path}>
                    <code>{e.path}</code>: {e.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={testIds.obsidianImport.confirm}
              onClick={handleConfirm}
              className="px-3 py-1 border border-black bg-blue-700 text-white hover:bg-blue-800"
            >
              Confirm import
            </button>
            <button
              type="button"
              data-testid={testIds.obsidianImport.reset}
              onClick={handleReset}
              className="px-3 py-1 border border-black bg-gray-200 hover:bg-gray-300"
            >
              Start over
            </button>
          </div>
        </section>
      )}

      {phase === 'committing' && (
        <section
          data-testid={testIds.obsidianImport.committing}
          className="p-4 border border-gray-700 bg-gray-50"
        >
          <p className="font-semibold mb-1">Saving to your portal…</p>
          <p className="text-gray-800">
            Writing {result?.notes.length ?? 0} note(s) and{' '}
            {result?.assets.length ?? 0} asset(s) to Convex. This usually
            takes a few seconds.
          </p>
        </section>
      )}

      {phase === 'committed' && commitResult && (
        <section
          data-testid={testIds.obsidianImport.committed}
          className="p-4 border-2 border-green-700 bg-green-50"
        >
          <h2 className="font-semibold text-green-900 mb-2">
            ✓ Import complete
          </h2>
          <ul className="list-disc list-inside space-y-1 mb-3 text-black">
            <li>
              <span data-testid={testIds.obsidianImport.committedNotesCount}>
                {commitResult.counts.notes}
              </span>{' '}
              note(s) saved
            </li>
            <li>
              <span data-testid={testIds.obsidianImport.committedLinksCount}>
                {commitResult.counts.links}
              </span>{' '}
              link(s) saved
            </li>
            <li>
              <span data-testid={testIds.obsidianImport.committedAssetsCount}>
                {commitResult.counts.assets}
              </span>{' '}
              asset(s) saved (metadata only)
            </li>
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={testIds.obsidianImport.openVault}
              onClick={handleOpenVault}
              className="px-3 py-1 border border-black bg-blue-700 text-white hover:bg-blue-800"
            >
              Open vault
            </button>
            <button
              type="button"
              data-testid={testIds.obsidianImport.importAnother}
              onClick={handleReset}
              className="px-3 py-1 border border-black bg-gray-200 hover:bg-gray-300"
            >
              Import another
            </button>
          </div>
        </section>
      )}

      {phase === 'error' && (
        <section className="p-4 border border-red-700 bg-red-50">
          <h2 className="font-semibold text-red-800 mb-2">Import failed</h2>
          <p className="text-red-800 mb-3">{errorMessage}</p>
          <button
            type="button"
            data-testid={testIds.obsidianImport.reset}
            onClick={handleReset}
            className="px-3 py-1 border border-black bg-gray-200 hover:bg-gray-300"
          >
            Try again
          </button>
        </section>
      )}

      {/* Bundled SharePoint folder picker. Opens when the user clicks
          "Pick a SharePoint folder" and no custom onPickSharePointFolder
          prop was supplied. */}
      <SharePointFolderPicker
        open={sharePointPickerOpen}
        onCancel={() => setSharePointPickerOpen(false)}
        onPick={handleSharePointPicked}
      />
    </div>
  );
}

interface PathCardProps {
  kind: 'sharepoint' | 'fsap' | 'dragdrop' | 'zip';
  title: string;
  description: string;
  buttonLabel: string;
  onClick?: () => void;
  disabled?: boolean;
  unsupportedTestId?: string;
  children?: React.ReactNode;
}

function PathCard({
  kind,
  title,
  description,
  buttonLabel,
  onClick,
  disabled,
  unsupportedTestId,
  children,
}: PathCardProps) {
  return (
    <section
      data-testid={testIds.obsidianImport.pathCard(kind)}
      className="p-3 border border-gray-700 bg-gray-50"
    >
      <h2 className="font-semibold mb-1">{title}</h2>
      <p className={unsupportedTestId ? 'text-gray-700 mb-2' : 'mb-2'}>
        <span data-testid={unsupportedTestId}>{description}</span>
      </p>
      {buttonLabel && (
        <button
          type="button"
          data-testid={testIds.obsidianImport.pathButton(kind)}
          onClick={onClick}
          disabled={disabled}
          className={`px-3 py-1 border border-black ${
            disabled
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          {buttonLabel}
        </button>
      )}
      {children}
    </section>
  );
}
